import { memo, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { CircleCheckIcon, ClockIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { integrationIconByType } from '@/components/atoms/Icons/integrationIconByType';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { cn } from '@/lib/utils';
import { useConversationEvents } from '@/modules/humanChat/hooks/useConversationEvents';
import {
  useConversationDetail,
  useConversationMutations,
} from '@/modules/humanChat/hooks/useConversations';
import { useOpenNewMessageModal } from '@/modules/humanChat/hooks/useOpenNewMessageModal';
import { useTemplateMessage } from '@/modules/humanChat/hooks/useTemplateMessage';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectSessionId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { ConversationEvent } from '@/shared/types/chat';
import { IntegrationType, ZendeskConfig } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { EventName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';
import { DIRECT_MESSAGE_INTEGRATION_TYPES } from '@/util/outboundIntegrations';

import { CHANNELS_WITHOUT_LIVE_CHAT } from './constants';
import { useChatBox } from './useChatBox';
import { FileUploadChatBoxView } from '../FileUploadChatBox/FileUploadChatBox';
import { getLiveChatUrl } from '../helpers';
import ScheduledMessagesList from '../ScheduledMessages/ScheduledMessagesList';

type ChatBoxStateCardProps = {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  cta?: ReactNode;
};

const ChatBoxStateCard = ({ icon, title, description, cta }: ChatBoxStateCardProps) => (
  <div className="flex flex-1 flex-col items-center justify-center">
    {icon}
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-sm font-semibold text-foreground">{title}</span>
      {description}
    </div>
    {cta}
  </div>
);

type ChatBoxProps = { onAddNote: () => void };

const ChatBox = memo(({ onAddNote }: ChatBoxProps) => {
  const chatBox = useChatBox();
  const { isClosed, isLoading, isUnavailable } = chatBox;

  const { t } = useTranslation();
  const { handleClick: handleTemplateClick, showTemplateMessage } = useTemplateMessage();

  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { conversation, isExpired, isResolved, isSpam, isTrash, getStatus } = useConversationDetail(
    { deskId, sessionId }
  );
  const { reopenConversation } = useConversationMutations({ deskId, sessionId });

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  const { openNewMessageModal: openModal } = useOpenNewMessageModal({ conversation });
  const openNewMessageModal = useCallback(() => {
    trackEvent(EventName.ClickNewMessageBottomBanner);
    openModal();
  }, [openModal]);

  const { events } = useConversationEvents(deskId, sessionId);

  const { integration } = useIntegrations(deskId, conversation?.integration_id);
  const subdomain = (integration?.config as ZendeskConfig)?.subdomain;
  const isUnsupportedChannel = CHANNELS_WITHOUT_LIVE_CHAT.includes(
    conversation?.channel as IntegrationType
  );

  const isClosedState = isResolved || isSpam || isTrash || isExpired;
  const canSendNewMessage =
    canWrite && DIRECT_MESSAGE_INTEGRATION_TYPES.includes(conversation?.channel as IntegrationType);

  const lastResolveEvent = events.findLast((e) => e.type === ConversationEvent.RESOLVE);
  const isVisitorResolved =
    isResolved && lastResolveEvent != null && lastResolveEvent.source_id == null;
  const visitorName = conversation?.context?.user?.display_name ?? t('common.visitor');

  const { label: closedLabel, reason: closedReason } = useMemo((): {
    label: string;
    reason: string | null;
  } => {
    if (isVisitorResolved) {
      return {
        label: t('conversation.resolved'),
        reason: t('conversation.reason_visitor_ended', { 0: visitorName }),
      };
    }
    if (isExpired && isResolved) {
      return {
        label: t('conversation.resolved'),
        reason: t('conversation.reason_session_expired'),
      };
    }
    if (isExpired) {
      return { label: t('conversation.expired'), reason: t('conversation.reason_session_closed') };
    }
    return { label: t('conversation.resolved'), reason: null };
  }, [isVisitorResolved, isExpired, isResolved, visitorName, t]);

  const [reopenTimeLeft, setReopenTimeLeft] = useState<string | null>(null);

  useEffect(() => {
    const expiredAt = conversation?.state?.expired_at;
    if (!expiredAt || isExpired) {
      setReopenTimeLeft(null);
      return;
    }

    const deadline = new Date(expiredAt).getTime();
    let id: ReturnType<typeof setTimeout>;

    const compute = () => {
      const ms = deadline - Date.now();
      if (ms <= 0) {
        setReopenTimeLeft(null);
        return;
      }
      if (ms < 60_000) {
        setReopenTimeLeft(`${Math.ceil(ms / 1_000)}s`);
        id = setTimeout(compute, 1_000);
      } else {
        const hours = Math.floor(ms / 3_600_000);
        const minutes = Math.floor((ms % 3_600_000) / 60_000);
        const parts: string[] = [];
        if (hours > 0) {
          parts.push(`${hours}h`);
        }
        parts.push(`${minutes}m`);
        setReopenTimeLeft(parts.join(' '));
        // schedule next tick at the top of the next minute
        id = setTimeout(compute, ms % 60_000 || 60_000);
      }
    };

    compute();
    return () => clearTimeout(id);
  }, [conversation?.state?.expired_at, isExpired]);

  const renderCta = () => {
    if (isExpired) {
      if (!canSendNewMessage) {
        return null;
      }
      return (
        <Button
          variant="link"
          size="sm"
          className="mt-2 text-primary"
          onClick={openNewMessageModal}
        >
          {t('conversation.new_message.title')}
        </Button>
      );
    }
    if (canWrite && !isSpam && !isTrash) {
      return (
        <Button variant="link" size="sm" className="mt-2 text-primary" onClick={reopenConversation}>
          {t('conversation.reopen')}
        </Button>
      );
    }
    return null;
  };

  const renderChatArea = () => {
    if (sessionId && getStatus === 'pending') {
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-2">
          <Skeleton className="h-4 w-4 rounded-full" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-48" />
        </div>
      );
    }
    if (isClosedState) {
      return (
        <ChatBoxStateCard
          icon={<CircleCheckIcon size={16} className="mb-2 text-success" />}
          title={closedLabel}
          description={
            <>
              {closedReason && (
                <span className="text-sm text-muted-foreground">{closedReason}</span>
              )}
              {reopenTimeLeft && (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {t('conversation.reopen_timer', { 0: reopenTimeLeft })}
                </span>
              )}
            </>
          }
          cta={renderCta()}
        />
      );
    }
    if (isUnsupportedChannel) {
      const channel = conversation?.channel as IntegrationType;
      const ChannelIcon = integrationIconByType[channel];
      const channelName = t(getChannelKey(channel));
      const channelUrl = getLiveChatUrl(channel, subdomain);
      return (
        <ChatBoxStateCard
          icon={ChannelIcon && <ChannelIcon size={20} className="mb-2 text-muted-foreground" />}
          title={t('chatBox.reply_in_channel', { 0: channelName })}
          cta={
            channelUrl && (
              <Button variant="link" size="sm" className="mt-2 text-primary" asChild>
                <a href={channelUrl} target="_blank" rel="noopener noreferrer">
                  {t('chatBox.open_channel', { 0: channelName })}
                </a>
              </Button>
            )
          }
        />
      );
    }
    // Past the 24h window the composer is replaced by a card that routes into
    // the template modal — the only way to reach the customer again.
    if (showTemplateMessage) {
      return (
        <ChatBoxStateCard
          icon={<ClockIcon size={16} className="mb-2 text-muted-foreground" />}
          title={t('chatBox.whatsapp_window_closed')}
          description={
            <span className="max-w-xs text-center text-sm text-muted-foreground">
              {t('chatBox.whatsapp_window_reason')}
            </span>
          }
          cta={
            <Button
              variant="link"
              size="sm"
              className="mt-2 text-primary"
              onClick={() => handleTemplateClick('send')}
            >
              {t('chatBox.browse_templates')}
            </Button>
          }
        />
      );
    }
    return (
      <form
        className={cn(
          'relative flex flex-1 flex-col pt-3 pr-2.5 pb-2.5 pl-3',
          (isUnavailable || isClosed || isLoading) && 'pointer-events-none border-border bg-muted',
          !canWrite && 'cursor-not-allowed [&>div]:pointer-events-none'
        )}
      >
        <FileUploadChatBoxView chatBox={chatBox} onAddNote={onAddNote} />
      </form>
    );
  };

  return (
    <div className="flex flex-col px-4 pb-6">
      {/* Pending messages can survive an internal close (brain/rules/spam
          auto-detect) and outlive the 24h WhatsApp window — keep them visible
          and cancellable above the closed / window-closed cards, since both
          replace the composer that normally hosts the list and resolving stays
          blocked while any are pending. The list self-hides when empty. */}
      {(isClosedState || showTemplateMessage) && <ScheduledMessagesList variant="inline" />}
      <section className="mx-auto mt-4 flex min-h-35 w-full max-w-3xl flex-col rounded-xl border border-border bg-background text-sm leading-5 font-normal">
        {renderChatArea()}
      </section>
    </div>
  );
});

ChatBox.displayName = 'ChatBox';

export default ChatBox;
